/* =====================================================================
 * UserGuidePage — 기능별 사용자 가이드 문서 뷰어. (`/user-guide`)
 *
 * 진입점은 사이드바 하단 고정 배너 하나뿐이다. 좌측 목차는
 * `useSidebarNavModel` 이 돌려준 **메인 사이드 패널의 실제 항목 목록**을
 * 그대로 비춘다 — 테넌트가 모드를 끄거나 사용자에게 권한이 없어 사이드바에
 * 안 뜨는 항목은 가이드에도 안 뜬다. 목차를 따로 하드코딩하면 모드를
 * 추가할 때 한쪽만 고쳐도 타입 에러가 나지 않아 조용히 어긋난다.
 *
 * 본문은 문서 전용 `GuideArticle` 로 그린다(채팅 렌더러의 코드블록 머리줄 ·
 * 복사 버튼 같은 대화용 장식이 문서에 섞이지 않도록). 문서는
 * `docs/<항목>.md` 로 두고 `docs/index.ts` 의 `USER_GUIDE_DOCS` 에 항목 키별로
 * 등록한다. 등록되지 않은 항목은 "준비중입니다" 로 떨어진다.
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Menu } from 'lucide-react';
import { useChatStore, useWorkspaceNavigationStore } from '@auw/stores';

import { useSidebarNavModel } from '../sidebar/hooks/useSidebarNavModel';
import { USER_GUIDE_DOCS } from './docs';
import { GuideArticle } from './GuideArticle';
import { GuideSiblings } from './GuideSiblings';
import { GuideToc } from './GuideToc';
import { buildGuideOutline, type GuideHeading } from './guideOutline';
import { modeEntry, type GuideSection } from './guideModel';
import { cancelGuideSettle, settleOnGuideHeading, useActiveHeading } from './useActiveHeading';

/* ------------------------------------------------------------------ */
/* Styles — 목차 패널 스타일은 `GuideToc.styles.ts`                      */
/* ------------------------------------------------------------------ */

const Page = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

/* `WorkspaceModeLayout` 의 ContentPane — 접힘 토글 버튼을 띄우는 기준 박스. */
const DocPane = styled.main`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

/* `WorkspaceModeLayout` 의 CollapsedSidebarToggleSlot + Btn. 목차를 접었을 때
 * 다시 펼 수 있는 유일한 버튼이라 본문 위에 떠 있다. */
const CollapsedTocToggleSlot = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const CollapsedTocToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(33,33,33,0.72)' : 'rgba(255,255,255,0.76)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#fff' : '#000')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 18px rgba(0,0,0,0.24)'
      : '0 8px 18px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(58,58,58,0.86)' : 'rgba(255,255,255,0.92)'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

const DocScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/* 목차를 접으면 본문이 왼쪽 끝까지 차오르는데, 그 자리에 떠 있는 펼치기
 * 버튼과 글이 겹친다. WorkspaceActionHeader 가 padding-left 를 48px 로
 * 넓히는 것과 같은 회피. */
const DocInner = styled.article<{ $tocOpen: boolean }>`
  max-width: 760px;
  margin: 0 auto;
  padding: 32px 32px 64px;
  padding-left: ${({ $tocOpen }) => ($tocOpen ? '32px' : '48px')};
  transition: padding-left ${({ theme }) => theme.transitions.fast};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px 16px 48px;
  }
`;

const DocEyebrow = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.brand};
`;

const DocTitle = styled.h1`
  margin: 6px 0 24px;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DocPreparing = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const NO_HEADINGS: readonly GuideHeading[] = [];

/* 문서가 없을 때 목차에 "준비 중" 을 보여줄 섹션. 구매 워크스페이스 문서는
 * 한꺼번에 준비 중이고, 나머지 섹션은 문서를 하나씩 채워 넣는 중이라 표시하지 않는다. */
const COMING_SOON_SECTIONS: ReadonlySet<string> = new Set(['purchaseWorkspace']);

/* 공유 주소: `/user-guide?doc=agent#section-3`.
 * 항목 키(`mode:agent`)를 그대로 쓰면 `mode%3Aagent` 로 인코딩돼 읽기 어려워서
 * 사람이 읽을 수 있는 이름으로 바꾼다. */
const DOC_QUERY = 'doc';
const docSlug = (key: string) => key.replace(/^mode:/, '').replace(':', '-');

interface GuideLink {
  slug?: string;
  headingId?: string;
}

function readGuideLink(): GuideLink {
  if (typeof window === 'undefined') return {};
  const url = new URL(window.location.href);
  return {
    slug: url.searchParams.get(DOC_QUERY) ?? undefined,
    headingId: url.hash ? decodeURIComponent(url.hash.slice(1)) : undefined,
  };
}

function guideUrl(key: string, headingId?: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set(DOC_QUERY, docSlug(key));
  url.hash = headingId ?? '';
  return url.toString();
}

export const UserGuidePage: React.FC = () => {
  const { t } = useTranslation();
  const mainSidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setMainSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const nav = useSidebarNavModel();

  /* 목차 접힘 상태는 통합 에이전트 목록 패널과 같은 스토어를 쓴다 — 모드별로
   * 나뉘어 localStorage 에 남으므로 페이지를 떠났다 돌아와도 유지된다. */
  const tocOpen = useWorkspaceNavigationStore((s) => s.isPageSidebarOpen('user-guide'));
  /* 셀렉터는 스토어 액션 자체를 꺼내고 호출부는 useCallback 으로 감싼다.
   * `(s) => () => ...` 처럼 셀렉터 안에서 함수를 새로 만들면 렌더마다 선택
   * 값이 바뀌어 무한 리렌더에 빠지고, 이후 라우트 전환도 화면에 반영되지 않는다. */
  const togglePageSidebar = useWorkspaceNavigationStore((s) => s.togglePageSidebar);
  const toggleToc = useCallback(() => togglePageSidebar('user-guide'), [togglePageSidebar]);

  const sections = useMemo<GuideSection[]>(() => {
    const built: GuideSection[] = [
      {
        key: 'workspace',
        title: t('sidebar.workspace'),
        entries: nav.workspaceItems.map((i) => modeEntry(i.mode, i.label)),
      },
      {
        key: 'purchaseWorkspace',
        title: t('sidebar.purchaseWorkspace'),
        entries: nav.purchaseWorkspaceItems.map((i) => modeEntry(i.mode, i.label)),
      },
      {
        key: 'specializedAgents',
        title: t('sidebar.specializedAgents'),
        entries: nav.specializedAgents.map((agent) => ({
          key: `specialized:${agent.id}`,
          label: agent.labelKey ? t(agent.labelKey) : agent.label,
        })),
      },
      {
        key: 'teamWorkspace',
        title: t('sidebar.teamWorkspace'),
        entries: nav.canManageTeamWorkspace
          ? nav.teamWorkspaceItems.map((i) => modeEntry(i.mode, i.label))
          : [],
      },
      {
        key: 'personal',
        title: t('sidebar.personal'),
        entries: nav.personalItems.map((i) => modeEntry(i.mode, i.label)),
      },
      {
        key: 'utility',
        title: t('sidebar.utility'),
        entries: nav.utilityItems.map((i) => modeEntry(i.mode, i.label)),
      },
      {
        key: 'marketplace',
        title: t('sidebar.marketplace'),
        entries: nav.marketplaceItems.map((i) => modeEntry(i.mode, i.label)),
      },
      {
        key: 'manage',
        title: t('sidebar.management'),
        /* 사이드바에서 관리자 콘솔은 `/admin/*` 링크라 mode 목록 밖에 따로 그려지지만,
         * 화면에서는 관리 항목들 바로 아래에 붙어 같은 묶음으로 읽힌다. 가이드 목차도
         * 관리 안에 둔다. 두 항목 모두 그 사용자에게 보일 때만 들어간다 —
         * 어드민 페이지는 서버 부서 허용목록(manageItems), 관리자 콘솔은 관리자 권한. */
        entries: [
          ...(nav.managementSectionVisible
            ? nav.manageItems.map((i) => modeEntry(i.mode, i.label))
            : []),
          ...(nav.adminConsoleVisible
            ? [{ key: 'admin-console', label: t('sidebar.adminConsole') }]
            : []),
        ],
      },
    ];

    return built.filter((section) => section.entries.length > 0);
  }, [nav, t]);

  const entries = useMemo(
    () => sections.flatMap((section) => section.entries),
    [sections],
  );

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const docScrollRef = useRef<HTMLDivElement>(null);
  /* 문서를 연 직후 이동할 절(검색 결과 클릭 · 공유 주소의 #절). 공유 주소로 처음
   * 들어올 때는 이미 그 절을 기대하므로 애니메이션 없이 바로 보여 준다. */
  const pendingHeadingRef = useRef<{ id: string; smooth: boolean } | null>(null);
  /* 페이지에 들어올 때의 주소. 목차가 채워진 뒤 한 번만 쓴다. */
  const initialLinkRef = useRef<GuideLink>(readGuideLink());

  /* 기본 선택. 공유 주소로 들어왔다면 그 문서를, 아니면 첫 항목을 연다.
   * 플래그 / 권한에 따라 목차가 나중에 채워질 수 있으므로(useAdminDashboardAccess
   * 응답 등) 선택이 비었거나 사라진 항목을 가리키는 동안에만 보정한다. */
  useEffect(() => {
    if (entries.length === 0) return;
    const link = initialLinkRef.current;
    const linked = link.slug ? entries.find((entry) => docSlug(entry.key) === link.slug) : undefined;
    if (linked) {
      initialLinkRef.current = {};
      pendingHeadingRef.current = link.headingId ? { id: link.headingId, smooth: false } : null;
      setActiveKey(linked.key);
      return;
    }
    setActiveKey((prev) =>
      prev && entries.some((entry) => entry.key === prev) ? prev : entries[0].key,
    );
  }, [entries]);

  /* 지금 보는 문서를 주소에 남긴다 — 새로고침하거나 주소를 복사해도 같은 문서가 열린다.
   * 라우터 상태(history.state)는 그대로 두고 주소만 바꾼다. */
  const addressKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeKey || typeof window === 'undefined') return;
    /* 처음 열린 문서는 들어온 주소의 #절을 그대로 둔다. 다른 문서로 옮겨 갈 때만 지운다. */
    const keepHash = addressKeyRef.current === null ? decodeURIComponent(window.location.hash.slice(1)) : '';
    addressKeyRef.current = activeKey;
    const next = guideUrl(activeKey, keepHash || undefined);
    if (next !== window.location.href) window.history.replaceState(window.history.state, '', next);
  }, [activeKey]);

  /* 같은 탭에서 주소의 #절만 바꿔 들어오면(새로고침 없음) 그 절로 이동한다. */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onHashChange = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (id) settleOnGuideHeading(docScrollRef.current, id);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const sectionLink = useCallback(
    (headingId: string) => (activeKey ? guideUrl(activeKey, headingId) : ''),
    [activeKey],
  );

  const activeEntry = entries.find((entry) => entry.key === activeKey) ?? null;
  const activeSectionTitle = useMemo(
    () => sections.find((section) => section.entries.some((entry) => entry.key === activeKey))?.title,
    [sections, activeKey],
  );
  const doc = activeEntry ? USER_GUIDE_DOCS[activeEntry.key] ?? null : null;

  /* 문서 끝의 이전/다음 링크 — 목차와 같은 순서로 넘어간다. */
  const activeIndex = activeEntry ? entries.indexOf(activeEntry) : -1;
  const siblingAt = (index: number) => {
    const entry = entries[index];
    return entry
      ? { label: entry.label, onSelect: () => selectEntry(entry.key), comingSoon: !USER_GUIDE_DOCS[entry.key] }
      : undefined;
  };

  /* 항목별 절 목록 — 왼쪽 목차의 펼침·검색과 현재 절 표시에 쓴다. */
  const headingsByKey = useMemo(() => {
    const map: Record<string, readonly GuideHeading[]> = {};
    for (const entry of entries) {
      const entryDoc = USER_GUIDE_DOCS[entry.key];
      if (entryDoc) map[entry.key] = buildGuideOutline(entryDoc.body).headings;
    }
    return map;
  }, [entries]);
  const activeHeadings = activeKey ? headingsByKey[activeKey] ?? NO_HEADINGS : NO_HEADINGS;

  const activeHeadingId = useActiveHeading(activeHeadings, docScrollRef);

  /* 다른 문서를 고르면 맨 위부터 읽는다. 검색 결과의 절을 눌렀다면 그 절로 간다.
   * 자식(GuideArticle)이 먼저 그려진 뒤 실행되므로 제목 요소가 이미 있다. */
  useEffect(() => {
    /* 아직 문서가 정해지지 않았으면 대기 중인 절을 소비하지 않는다 — 공유 주소로
     * 들어오면 첫 렌더에는 activeKey 가 비어 있고, 다음 렌더에서 문서가 열린다. */
    if (!activeKey) return undefined;
    const pending = pendingHeadingRef.current;
    pendingHeadingRef.current = null;
    if (pending) {
      /* 새 문서는 맨 위에서 출발해 그 절까지 내려간다 — 앞 문서의 스크롤 위치에서 시작하면
       * 엉뚱한 곳에서 움직이기 시작한다. */
      if (docScrollRef.current) docScrollRef.current.scrollTop = 0;
      return settleOnGuideHeading(docScrollRef.current, pending.id, { smooth: pending.smooth });
    }
    cancelGuideSettle(docScrollRef.current);
    docScrollRef.current?.scrollTo?.({ top: 0 });
    return undefined;
  }, [activeKey]);

  const selectEntry = useCallback((key: string, headingId?: string) => {
    if (key === activeKey) {
      if (headingId) {
        settleOnGuideHeading(docScrollRef.current, headingId);
      } else {
        cancelGuideSettle(docScrollRef.current);
        docScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    pendingHeadingRef.current = headingId ? { id: headingId, smooth: true } : null;
    setActiveKey(key);
  }, [activeKey]);

  return (
    <Page>
      <GuideToc
        sections={sections}
        activeKey={activeKey}
        onSelect={selectEntry}
        headingsByKey={headingsByKey}
        activeHeadingId={activeHeadingId}
        open={tocOpen}
        onToggle={toggleToc}
        mainSidebarOpen={mainSidebarOpen}
        onOpenMainSidebar={() => setMainSidebarOpen(true)}
        comingSoonSectionKeys={COMING_SOON_SECTIONS}
      />

      <DocPane>
        {!tocOpen && (
          <CollapsedTocToggleSlot>
            <CollapsedTocToggleBtn
              type="button"
              onClick={toggleToc}
              title={t('workspace.expandPageSidebar')}
              aria-label={t('workspace.expandPageSidebar')}
            >
              <Menu size={16} />
            </CollapsedTocToggleBtn>
          </CollapsedTocToggleSlot>
        )}
        <DocScroll ref={docScrollRef}>
          {activeEntry && doc ? (
            <GuideArticle
              key={activeEntry.key}
              markdown={doc.body}
              updatedAt={doc.updatedAt}
              title={activeEntry.label}
              section={activeSectionTitle || undefined}
              guideTitle={t('userGuide.title')}
              tocOpen={tocOpen}
              scrollRootRef={docScrollRef}
              activeHeadingId={activeHeadingId}
              sectionLink={sectionLink}
              prev={activeIndex > 0 ? siblingAt(activeIndex - 1) : undefined}
              next={activeIndex >= 0 ? siblingAt(activeIndex + 1) : undefined}
            />
          ) : (
            <DocInner $tocOpen={tocOpen}>
              <DocEyebrow>{t('userGuide.title')}</DocEyebrow>
              <DocTitle>{activeEntry?.label ?? t('userGuide.title')}</DocTitle>
              {activeEntry && <DocPreparing>{t('userGuide.preparing')}</DocPreparing>}
              {/* 준비 중인 페이지에서도 목차 순서대로 앞뒤 문서로 넘어갈 수 있게 한다. */}
              <GuideSiblings
                prev={activeIndex > 0 ? siblingAt(activeIndex - 1) : undefined}
                next={activeIndex >= 0 ? siblingAt(activeIndex + 1) : undefined}
              />
            </DocInner>
          )}
        </DocScroll>
      </DocPane>
    </Page>
  );
};
